import { useRef, useState } from "react";
import { ImagePlus } from "lucide-react";
import { StatusBanner } from "@/components/StatusBanner";
import { useMediaUpload } from "@/features/identity/hooks/useMediaUpload";
import { validateMediaFile } from "@/features/identity/validation";

function Thumb({ kind, src }) {
  const base = "flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border";
  if (!src) {
    return (
      <span data-testid={`${kind}-empty-thumb`} className={`${base} border-dashed bg-background text-muted-foreground`}>
        <ImagePlus className="h-6 w-6" aria-hidden="true" />
      </span>
    );
  }
  return (
    <span className={`${base} ${kind === "logo" ? "bg-white p-2" : "bg-muted"}`}>
      <img src={src} alt="" data-testid={`${kind}-preview-image`} className={`h-full w-full ${kind === "logo" ? "object-contain" : "object-cover"}`} />
    </span>
  );
}

export function MediaUploader({ kind }) {
  const media = useMediaUpload(kind);
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const local = validateMediaFile(kind, file) ? null : URL.createObjectURL(file);
    setPreview(local);
    await media.upload(file);
    setPreview(null);
    if (local) URL.revokeObjectURL(local);
  };

  return (
    <div data-testid={`${kind}-uploader`} className="flex flex-col gap-5 sm:flex-row sm:items-start">
      <Thumb kind={kind} src={preview || media.current} />
      <div className="min-w-0 flex-1 space-y-3">
        <div>
          <p className="font-medium">{media.cfg.label}</p>
          <p className="mt-1 text-xs text-muted-foreground">{media.cfg.hint} {media.cfg.typesLabel} · up to 5 MB.</p>
        </div>
        {media.busy && (
          <div data-testid={`${kind}-upload-progress`} className="space-y-2">
            <div className="mw-progress"><span /></div>
            <p className="mw-eyebrow">{media.status === "uploading" ? "Uploading" : "Removing"}</p>
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={media.busy} data-testid={`${kind}-upload-button`} className="mw-btn mw-btn-ghost">
            {media.current ? "Replace" : "Upload"}
          </button>
          {media.current && (
            <button type="button" onClick={media.remove} disabled={media.busy} data-testid={`${kind}-remove-button`} className="mw-btn mw-btn-ghost hover:border-destructive hover:text-destructive">
              Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept={media.cfg.types.join(",")} onChange={onFile} className="hidden" data-testid={`${kind}-file-input`} />
        {media.message && (
          <StatusBanner variant={media.status === "error" ? "error" : "success"} testId={`${kind}-upload-status`}>
            {media.message}
          </StatusBanner>
        )}
      </div>
    </div>
  );
}
